'use client'

import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { Link } from '@/lib'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Lock, Eye, EyeOff, Mail, ArrowLeft } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

interface PasswordProtectionProps {
  children: React.ReactNode
}

export default function PasswordProtection({ children }: PasswordProtectionProps) {
  const { isAuthenticated, isLoading: authLoading, authenticate } = useAuth()
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [requestEmail, setRequestEmail] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const navigate = useNavigate()

  const handleRequestAccess = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!requestEmail.trim()) {
      setError('Please enter your email address')
      return
    }

    setIsSendingEmail(true)
    setError('')

    try {
      const formData = new FormData()
      formData.append('email', requestEmail)
      formData.append('name', 'Portfolio Access Request')
      formData.append(
        'message',
        `Hello Dixon,\n\nI would like to request access to your portfolio.\n\nEmail: ${requestEmail}\n\nBest regards`
      )
      formData.append('_subject', `[${requestEmail}] wants access to dixonbordiano.com`)
      formData.append('_replyto', requestEmail)

      const response = await fetch('https://formspree.io/f/xeorldnn', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      })

      if (response.ok) {
        setEmailSent(true)
        setRequestEmail('')
        setTimeout(() => setEmailSent(false), 5000)
      } else {
        const errorData = await response.json().catch(() => ({}))
        console.error('Formspree error:', response.status, errorData)
        throw new Error(`Server error: ${response.status}`)
      }
    } catch (err) {
      console.error('Email sending error:', err)
      setError('Failed to send access request. Please try again or email dbordiano@gmail.com directly.')
    }

    setIsSendingEmail(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const result = await authenticate(password)

      if (result.success) {
        navigate('/ProfessionalProjects')
        window.location.reload()
      } else {
        setError(result.message)
      }
    } catch {
      setError('An error occurred during authentication. Please try again.')
    }

    setIsLoading(false)
  }

  if (authLoading) {
    return (
      <div className="min-h-screen h-screen flex items-center justify-center px-4 py-6">
        <div className="text-center">
          <div className="w-16 h-16 aspect-square bg-gradient-to-br from-[var(--brutal-mustard)] to-[var(--brutal-kraft)] rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse flex-shrink-0">
            <Lock className="w-8 h-8 text-[var(--brutal-ink)]" />
          </div>
          <p className="text-[var(--brutal-muted)]">Loading...</p>
        </div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <div className="relative">{children}</div>
  }

  return (
    <div className="min-h-screen h-screen flex items-center justify-center px-4 py-6 sm:px-6 sm:py-8 md:px-8 md:py-12">
      <Card className="w-full max-w-md brutal-panel p-6 md:p-8">
        <div className="mb-6">
          <Link
            to="/Home"
            className="inline-flex items-center text-[var(--brutal-muted)] hover:text-[var(--brutal-ink)] transition-colors duration-200 text-sm md:text-base group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-200" aria-hidden="true" />
            <span>Back to Home</span>
          </Link>
        </div>

        <div className="text-center mb-8">
          <div className="w-16 h-16 aspect-square bg-gradient-to-br from-[var(--brutal-mustard)] to-[var(--brutal-kraft)] rounded-full flex items-center justify-center mx-auto mb-4 flex-shrink-0">
            <Lock className="w-8 h-8 text-[var(--brutal-ink)]" aria-hidden="true" />
          </div>
          <h1 className="text-xl md:text-2xl font-bold text-[var(--brutal-ink)] mb-2 break-words">
            Portfolio Access
          </h1>
          <p className="text-[var(--brutal-muted)] text-sm md:text-base break-words px-2">
            Please enter the password to view this portfolio
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-[var(--brutal-ink)]">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] placeholder:text-[var(--brutal-muted)] focus:border-[var(--brutal-ink)] pr-10"
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0 text-[var(--brutal-muted)] hover:text-[var(--brutal-ink)]"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <Button type="submit" disabled={isLoading} className="w-full brutal-btn font-medium">
            {isLoading ? 'Verifying...' : 'Access Portfolio'}
          </Button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[var(--brutal-ink)]" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-[var(--brutal-panel)] text-[var(--brutal-muted)]">or</span>
          </div>
        </div>

        <form onSubmit={handleRequestAccess} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="requestEmail" className="text-[var(--brutal-ink)]">
              Email Address
            </Label>
            <Input
              id="requestEmail"
              type="email"
              value={requestEmail}
              onChange={(e) => {
                setRequestEmail(e.target.value)
                if (error) setError('')
              }}
              placeholder="your@email.com"
              disabled={isSendingEmail || emailSent}
              required
              autoComplete="email"
              className="w-full bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] placeholder:text-[var(--brutal-muted)] focus:border-[var(--brutal-ink)] disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <Button
            type="submit"
            disabled={isSendingEmail || emailSent}
            className="w-full brutal-btn font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mail className="w-4 h-4 mr-2" aria-hidden="true" />
            {isSendingEmail ? 'Sending...' : emailSent ? 'Access Request Sent!' : 'Request Access'}
          </Button>

          {emailSent && (
            <p className="text-[var(--brutal-success)] text-sm text-center">
              ✓ Access request sent successfully! You&apos;ll receive a response within 24 hours.
            </p>
          )}
        </form>

        {error && (
          <div className="mt-4 text-red-800 text-sm text-center bg-red-500/10 border border-red-500/20 rounded-lg p-3 break-words">
            {error}
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-[var(--brutal-muted)] text-xs md:text-sm break-words px-2">
            This portfolio is password protected for privacy.
            <br />
            Don&apos;t have the password? Use the email form above to request access.
          </p>
        </div>
      </Card>
    </div>
  )
}
